import { Box } from '@mui/material';
import { useAtom } from 'jotai';
import { Children, isValidElement, type ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { ContentWrapper } from '../../../components/content-wrapper';
import { lastVisitedWizardStepAtom } from '../../../stores/goods-receiving-wizard.store';
import { WizardNavigation } from '../components/wizard-navigation';
import { WizardStepTransition } from '../components/wizard-step-transition';
import { WizardStepper } from '../components/wizard-stepper';
import {
  isWizardRegisterPath,
  isWizardStep,
  resolveWizardDirection,
  type WizardStep,
} from '../hooks/wizard-steps';

interface WizardLayoutProps {
  children: ReactNode;
}

interface WizardLayoutContent {
  stepContent: ReactNode[];
  fixedNavigation: ReactNode[];
}

// The fixed bottom navigation must stay outside the translated step content: a
// transformed ancestor would anchor it to the content box instead of the viewport.
function separateFixedNavigation(children: ReactNode): WizardLayoutContent {
  const layoutContent: WizardLayoutContent = { stepContent: [], fixedNavigation: [] };
  for (const child of Children.toArray(children)) {
    if (isValidElement(child) && child.type === WizardNavigation) {
      layoutContent.fixedNavigation.push(child);
    } else {
      layoutContent.stepContent.push(child);
    }
  }
  return layoutContent;
}

function useWizardStepDirection(currentStep: WizardStep) {
  const [lastVisitedStep, setLastVisitedStep] = useAtom(lastVisitedWizardStepAtom);
  // Resolved once per mount: updating the atom below must not flip it mid-entrance.
  const [direction] = useState(() => resolveWizardDirection(lastVisitedStep, currentStep));

  useEffect(() => {
    if (isWizardStep(currentStep)) {
      setLastVisitedStep(currentStep);
    }
    return () => {
      // On unmount the router already points at the next page: leaving the
      // wizard resets the history so a new registration does not slide backward.
      if (!isWizardRegisterPath(window.location.pathname)) {
        setLastVisitedStep(null);
      }
    };
  }, [currentStep, setLastVisitedStep]);

  return direction;
}

export function WizardLayout({ children }: WizardLayoutProps) {
  const location = useLocation();
  // Extract step from pathname: /goods-receiving/register/supplier -> supplier
  const pathSegments = location.pathname.split('/');
  const currentStep = pathSegments[pathSegments.length - 1] as WizardStep;
  const direction = useWizardStepDirection(currentStep);
  const { stepContent, fixedNavigation } = separateFixedNavigation(children);

  return (
    <ContentWrapper>
      <WizardStepper currentStep={currentStep} />
      <Box sx={{ pb: 10 }}>
        {/* storage-step mounts this layout for its spinner and its content at the same position: the entrance plays on the spinner. */}
        <WizardStepTransition direction={direction}>{stepContent}</WizardStepTransition>
        {fixedNavigation}
      </Box>
    </ContentWrapper>
  );
}
